import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { State } from '../../../reducers';
import { setAnuladoFactura, setStatusModalAnular, setStatusModalEnvio, setStatusModalImprimir, setStatusModalOpciones } from '../../../actions/historial/historial.actions';

import ModalHistorialAcciones from '../../../components/Modals/ModalsFacturacion/ModalHistorialAcciones';
import ModalEnviarEmail from '../../../components/Modals/ModalsFacturacion/ModalEnviarEmail';
import ModalAnularFactura from '../../../components/Modals/ModalsFacturacion/ModalAnularFactura';
import ModalImprimirFactura from '../../../components/Modals/ModalsFacturacion/ModalImprimirFactura';

import { actionsHistorialFiltro, actionsOperaciones } from '../../../actions/action-creators';
import { generalOpenModalMessage } from '../../../actions/general.actions';
import { emailInvoice } from '../../../utils/invoice/validateSendInvoice';
import { OPSendEmail } from '../../../interfaces/operaciones.interface';
import { useEffect, useMemo, useState } from 'react';
import { generateQRImageFromBits } from '../../../actions/operaciones/operaciones.actions';
import { getDocumentoContingencia } from '../../../actions/contingencia/contingencia.actions';
import { unirChipsArray } from '../../../utils/strings.utils';

const ModalsControl = () => {
  const modals = useSelector((state: State) => state.historial.modals);
  const {activeAnular, activeEnvioCorreo, activeOpciones, activePrint} = modals;
  const {activeFactura, qrFactura, anulado, itemsAnular, resultAnulaciones, formFilter, arrayFacturas} = useSelector((state: State) => state.historial);
  const {datos, config} = useSelector((state: State) => state.userLogin)
  
  const dispatch = useDispatch();

  const {sendEmailPDF, anularFactura} = bindActionCreators(actionsOperaciones, dispatch);
  const {historialHandleAnulaciones, getTransactions} = bindActionCreators(actionsHistorialFiltro, dispatch);

  const [versionDoc, setVersionDoc] = useState<'certificado' | 'contingencia'>('certificado');
  const [docContingencia, setDocContingencia] = useState<any>(null);

  const esContingencia = activeFactura?.esContingencia === true;
  const tieneCertificado = activeFactura?.tieneCertificado === true;

  useEffect(() => {
    setDocContingencia(null);
    // Mientras Hacienda no lo certifique no existe version certificada que mostrar.
    setVersionDoc(esContingencia && !tieneCertificado ? 'contingencia' : 'certificado');
  }, [activeFactura?.codigogeneracion]);

  const cambiarVersion = async (version: 'certificado' | 'contingencia') => {
    if (version === 'contingencia' && !docContingencia) {
      const doc: any = await dispatch(getDocumentoContingencia(activeFactura?.codigogeneracion, 'contingencia'));
      if (!doc?.documento) return;
      setDocContingencia(doc);
    }
    setVersionDoc(version);
  };

  const docActivo = useMemo(() => {
    if (versionDoc === 'contingencia' && docContingencia?.documento) {
      return {
        Documento: docContingencia.documento,
        Sello: docContingencia.selloRecibido,
        SelloBusqueda: docContingencia.selloBusqueda,
      };
    }

    return {
      Documento: activeFactura?.documento,
      Sello: activeFactura?.sellorecibido,
      SelloBusqueda: undefined,
    };
  }, [versionDoc, docContingencia, activeFactura]);

  const handleEnviarEmail = async (formEnvio: emailInvoice) => {
    const sendEmail: OPSendEmail = {
      Correo: formEnvio.email,
      Sello: docActivo.SelloBusqueda ?? activeFactura?.sellorecibido,
      IdSucursalDoc: activeFactura?.DetallesDTE?.identificacion?.IDsucursalDoc || "-1",
      idSucursal: datos?.sucursal_seleccionada,
      idEmpresa: `${datos?.codigo_empresa}`
    }
    if (await sendEmailPDF(sendEmail)) {
      dispatch(generalOpenModalMessage(`Enviar por correo electrónico`, `El correo electrónico fue enviado exitosamente.`))
      dispatch(setStatusModalEnvio(false));
    }
  }

  const handleAnular = () => {
    let arrayAnular;

    if (activeFactura) arrayAnular = [activeFactura];  
    else arrayAnular = arrayFacturas.filter((factura) => itemsAnular.includes(factura?.DetallesDTE?.identificacion?.numeroControl));

    anularFactura(arrayAnular);
  }

  useEffect(() => {
    if (resultAnulaciones.length > 0) {
      historialHandleAnulaciones();
      getTransactions(formFilter);
    }
  }, [resultAnulaciones]);

  const emailReceptor = useMemo(() => {
    if (!activeFactura) { return ''; }
    const {DetallesDTE: {sujetoExcluido, receptor, identificacion}} = activeFactura;
    const emailsEnvio = identificacion?.ReceptorEmailsEnvio;
    if (emailsEnvio) {
      return emailsEnvio.split(',').map((e: string) => e.trim()).filter((e: string) => e !== '');
    }

    const clienteReceptor = receptor ?? sujetoExcluido;
    const correo = (clienteReceptor?.correo ?? '').trim();
    return correo === '' ? [] : [correo];
  }, [activeFactura])

  return (
    <>
      {(activeOpciones) && 
        (<ModalHistorialAcciones
          factura={activeFactura}
          qrCode={generateQRImageFromBits(qrFactura?.replace("data:image/jpeg;base64,", ""))}
          anulado={anulado} 
          version={versionDoc}
          onCambiarVersion={cambiarVersion}
          docActivo={docActivo}
          docContingencia={docContingencia}
          onCancel={() => dispatch(setStatusModalOpciones(false))}
        />)
      }
      {(activeEnvioCorreo) && 
        (<ModalEnviarEmail
          defaultEmail={''}
          chipsEmail={emailReceptor}
          showBackBtn={true}
          onBackBtn={() => dispatch(setStatusModalEnvio(false))} 
          onSendEmail={handleEnviarEmail}
        />)
      }
      {(activeAnular) && 
        (<ModalAnularFactura 
          closeModalEliminar={() => dispatch(setStatusModalAnular(false))}
          actionEliminar={handleAnular}
          detail={
            activeFactura 
            ? activeFactura?.DetallesDTE?.identificacion?.numeroControl
            : <ul>{itemsAnular.map((item) => <li>{item}</li>)}</ul>
          }
        />)
      }
      {(activePrint) &&
        (<ModalImprimirFactura
          documento={docActivo.Documento}
          sello={docActivo.Sello}
          selloBusqueda={docActivo.SelloBusqueda}
          estadoAnulacion={activeFactura?.estadoanulacion}
          modoTicket={modals.modoTicketPrint}
          onCancel={() => dispatch(setStatusModalImprimir(false))}
          onPrint={() => dispatch(setStatusModalImprimir(false))}
        />)
      }
    </>
  )
}

export default ModalsControl;
